// Utility to validate internal post-auth navigation without trusting a Host
// header or accepting browser URL-normalization tricks.
const RAW_ALLOWED = typeof process !== 'undefined' ? (process.env.NEXT_PUBLIC_ALLOWED_REDIRECT_PATHS || '') : '';
const ALLOWED = RAW_ALLOWED.split(',').map(s => s.trim()).filter(Boolean);
const INTERNAL_ORIGIN = 'https://platform.internal.invalid';
const DEFAULT_ALLOWED_PREFIXES = [
  '/console',
  '/hubs',
  '/reports',
  '/admin',
  '/invitation',
  '/api/external-auth/authorize',
  '/api/external-auth/continue',
] as const;

const hasDangerousPathEncoding = (pathname: string): boolean => {
  let candidate = pathname;
  for (let depth = 0; depth < 3; depth += 1) {
    if (/\\|[\u0000-\u001f\u007f]/.test(candidate) ||
        candidate.startsWith('//') || candidate.includes('://')) {
      return true;
    }
    try {
      const decoded = decodeURIComponent(candidate);
      if (decoded === candidate) return false;
      candidate = decoded;
    } catch {
      return true;
    }
  }
  return /%25/i.test(candidate) || /\\|[\u0000-\u001f\u007f]/.test(candidate);
};

const matchesAllowlist = (p: string): boolean => {
  if (p === '/' || DEFAULT_ALLOWED_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`))) {
    return true;
  }

  for (const rule of ALLOWED) {
    if (!rule) continue;
    // prefix style: /panel/*
    if (rule.endsWith('/*')) {
      const prefix = rule.slice(0, -2);
      if (p === prefix || p.startsWith(prefix + '/')) return true;
      continue;
    }

    // regex style: ^/panel\/.*$
    if (rule.startsWith('^')) {
      try {
        const re = new RegExp(rule);
        if (re.test(p)) return true;
      } catch {
        // invalid regex - skip
        continue;
      }
      continue;
    }

    // exact match
    if (p === rule) return true;
  }
  return false;
};

export const isSafeRedirect = (p?: string | null): boolean => {
  if (!p || p.length > 8_192 || p !== p.trim() || !/^\/(?!\/)/.test(p)) return false;
  if (/\\/.test(p.split('?', 1)[0] ?? '')) return false;

  let parsed: URL;
  try {
    parsed = new URL(p, INTERNAL_ORIGIN);
  } catch {
    return false;
  }

  if (parsed.origin !== INTERNAL_ORIGIN || parsed.username || parsed.password || parsed.hash) return false;
  if (hasDangerousPathEncoding(parsed.pathname)) return false;
  return matchesAllowlist(parsed.pathname);
};

export const sanitizeRedirect = (p?: string | null): string | undefined => {
  if (!p) return undefined;
  if (!isSafeRedirect(p)) return undefined;
  const parsed = new URL(p, INTERNAL_ORIGIN);
  if (parsed.origin === INTERNAL_ORIGIN) return `${parsed.pathname}${parsed.search}`;
  return undefined;
};

export const getAllowedRules = () => ALLOWED.slice();

// Mark as used to avoid "unused" warnings in some TypeScript setups
void getAllowedRules;
